import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Tooltip } from '@mui/material';

import { LAYER_NORM_CUSTOM, LAYER_NORM_NONE, NORMS } from '../../../../constants/layers.ts';
import { useAppDispatch, useAppSelector } from '../../../../hooks';
import {
  addMeshGroup,
  deleteMeshGroup,
  handleLayerClick,
  renameMeshGroup,
  setHoveredLayer,
  toggleLayerIllumination,
  toggleLayerLuminaireHolder,
  updateLayerCustomTargets,
  updateLayerDisplayName,
  updateLayerGroupId,
  updateLayerNorm,
  updateLayerType,
} from '../../../../store/reducers/workspace.reducer.ts';
import { selectActiveVersion } from '../../../../store/selectors/workspace.selector.ts';
import { greyP5 } from '../../../../theme.ts';
import { MeshGroup } from '../../../../types/mesh.ts';
import { ModelLayer } from '../../../../types/model.ts';
import { isCalculationStarted } from '../../../../utils/jobStatus.ts';
import { describeTargets } from '../../../../utils/photometricTargets';
import { MeshGroupTree } from '../../../common/MeshGroupTree';
import { CustomTargetsForm } from '../../../photometric-targets/CustomTargetsForm';
import { LightingStandardsInfo } from '../../../project-settings/components/LayerSidebar/LightingStandardsInfo';
import { LayerTypeIcon } from '../../../project-settings/components/LayerTypeIcon';
import { LAYER_TYPES } from '../../../project-settings/types';
import { PanelSearch, PanelSection } from '../PanelSection';

type SortKey = 'name' | 'type' | 'chl' | 'lmn';

/** Layer table: type, name, CHL / LMN toggles and the norm of each illuminated layer. */
export function LayersSection({ highlightToggles }: { highlightToggles: boolean }) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const modelLayers = useAppSelector((s) => s.workspace.modelLayers);
  const meshGroups = useAppSelector((s) => s.workspace.meshGroups);
  const hoveredLayer = useAppSelector((s) => s.workspace.hoveredLayer);
  const selectedLayer = useAppSelector((s) => s.workspace.selectedLayer);
  const canModify = !isCalculationStarted(useAppSelector(selectActiveVersion));

  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState<SortKey>('name');
  const [sortAsc, setSortAsc] = useState(true);
  const [editingLayerId, setEditingLayerId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState('');
  const [normLayer, setNormLayer] = useState<string | null>(null);
  const [typeMenu, setTypeMenu] = useState<{ layer: string; top: number; left: number } | null>(null);

  const filteredLayers = useMemo(() => {
    let layers = modelLayers;
    const q = search.trim().toLowerCase();
    if (q) {
      layers = layers.filter(
        (layer) =>
          layer.threeJsName.toLowerCase().includes(q) ||
          layer.displayName?.toLowerCase().includes(q) ||
          layer.originalName?.toLowerCase().includes(q),
      );
    }
    const dir = sortAsc ? 1 : -1;
    return [...layers].sort((a, b) => {
      switch (sortKey) {
        case 'name':
          return dir * (a.displayName || a.threeJsName).localeCompare(b.displayName || b.threeJsName);
        case 'type':
          return dir * a.layerType.localeCompare(b.layerType);
        case 'chl':
          return dir * (Number(b.canHoldLuminaire) - Number(a.canHoldLuminaire));
        case 'lmn':
          return dir * (Number(b.isIlluminated) - Number(a.isIlluminated));
      }
    });
  }, [modelLayers, search, sortKey, sortAsc]);

  // Keep the selected layer (picked in 3D or via the footer) in view.
  useEffect(() => {
    if (!selectedLayer) return;
    const frame = requestAnimationFrame(() => {
      document
        .querySelector(`[data-panel-layer-id="${CSS.escape(selectedLayer)}"]`)
        ?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    });
    return () => cancelAnimationFrame(frame);
  }, [selectedLayer]);

  // The type menu is positioned once, so close it when anything scrolls or resizes.
  useEffect(() => {
    if (!typeMenu) return;
    const close = () => setTypeMenu(null);
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    return () => {
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
    };
  }, [typeMenu]);

  const sortBy = (key: SortKey) => {
    if (sortKey === key) setSortAsc(!sortAsc);
    else {
      setSortKey(key);
      setSortAsc(true);
    }
  };
  const sortArrow = (key: SortKey) => (sortKey === key ? <span className="ml-0.5">{sortAsc ? '↑' : '↓'}</span> : null);

  const saveName = (uuid: string) => {
    if (editingName.trim()) dispatch(updateLayerDisplayName({ uuid, displayName: editingName.trim() }));
    setEditingLayerId(null);
  };

  const toggleChl = (e: React.MouseEvent, layer: ModelLayer) => {
    e.stopPropagation();
    if (canModify) dispatch(toggleLayerLuminaireHolder(layer.threeJsName));
  };

  const toggleLmn = (e: React.MouseEvent, layer: ModelLayer) => {
    e.stopPropagation();
    if (!canModify) return;
    dispatch(toggleLayerIllumination(layer.threeJsName));
    // Turning illumination off also clears the norm.
    if (layer.isIlluminated) dispatch(updateLayerNorm({ name: layer.threeJsName, norm: LAYER_NORM_NONE }));
  };

  const openNorms = (e: React.MouseEvent, layer: ModelLayer) => {
    e.stopPropagation();
    if (!canModify || !layer.isIlluminated) return;
    setTypeMenu(null);
    setNormLayer(normLayer === layer.threeJsName ? null : layer.threeJsName);
  };

  const openTypes = (e: React.MouseEvent<HTMLButtonElement>, layer: ModelLayer) => {
    e.stopPropagation();
    if (!canModify) return;
    setNormLayer(null);
    if (typeMenu?.layer === layer.threeJsName) {
      setTypeMenu(null);
      return;
    }
    const rect = e.currentTarget.getBoundingClientRect();
    const menuHeight = 44 + Object.keys(LAYER_TYPES).length * 36;
    const top =
      rect.bottom + 4 + menuHeight > window.innerHeight ? Math.max(8, rect.top - 4 - menuHeight) : rect.bottom + 4;
    setTypeMenu({ layer: layer.threeJsName, top, left: Math.min(rect.left, window.innerWidth - 196) });
  };

  const toggleClass = `relative w-9 h-5 rounded-full bg-black shrink-0 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-lime-400/60 ${
    canModify ? '' : 'opacity-60 cursor-not-allowed'
  } ${highlightToggles ? 'animate-highlight-toggle' : ''}`;

  const renderRow = (layer: ModelLayer) => {
    const isSelected = selectedLayer === layer.threeJsName;
    const isHovered = hoveredLayer === layer.threeJsName;
    const hasNorm = !!layer.selectedNorm && layer.selectedNorm !== LAYER_NORM_NONE;
    return (
      <div
        draggable
        onDragStart={(e) => e.dataTransfer.setData('text/layer-uuid', layer.uuid)}
        data-panel-layer-id={layer.threeJsName}
        className={`flex items-center gap-2.5 h-8 px-3 cursor-pointer scroll-mt-32 border-b border-white/[0.04] ${
          // Neutral, like the white outline of the selected layer in 3D (CHL is blue and LMN lime there).
          isSelected
            ? 'bg-white/[0.12] ring-1 ring-inset ring-white/50'
            : isHovered
              ? 'bg-white/[0.06]'
              : 'hover:bg-white/[0.04]'
        }`}
        onClick={() => dispatch(handleLayerClick({ threeJsName: layer.threeJsName, originalName: layer.originalName }))}
        onMouseEnter={() => dispatch(setHoveredLayer(layer.threeJsName))}
        onMouseLeave={() => dispatch(setHoveredLayer(null))}
      >
        <button
          type="button"
          onClick={(e) => openTypes(e, layer)}
          disabled={!canModify}
          title={t('workspace.layerTypeTitle', { type: layer.layerType })}
          className={`w-6 h-6 shrink-0 flex items-center justify-center rounded border transition hover:border-lime-500 disabled:opacity-60 disabled:cursor-not-allowed ${
            layer.layerType === 'Unspecified' ? 'bg-gray-600 border-gray-500' : 'border-gray-600'
          }`}
        >
          <LayerTypeIcon type={layer.layerType} style={{ width: 14, height: 14, color: 'white' }} />
        </button>

        <div className="flex-1 min-w-0">
          {editingLayerId === layer.uuid ? (
            <input
              type="text"
              value={editingName}
              onChange={(e) => setEditingName(e.target.value)}
              onBlur={() => saveName(layer.uuid)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') saveName(layer.uuid);
                else if (e.key === 'Escape') setEditingLayerId(null);
              }}
              onClick={(e) => e.stopPropagation()}
              autoFocus
              className="w-full text-[13px] bg-black border border-lime-500 rounded px-1 text-white focus:outline-none"
            />
          ) : (
            <span
              className={`block truncate text-[13px] ${
                isSelected ? 'text-white font-medium' : 'text-white'
              } ${canModify ? '' : 'opacity-70'}`}
              onDoubleClick={(e) => {
                if (!canModify) return;
                e.stopPropagation();
                setEditingLayerId(layer.uuid);
                setEditingName(layer.displayName || layer.threeJsName);
              }}
              title={canModify ? t('workspace.layerNameDoubleClick') : t('workspace.cannotEditLayerName')}
            >
              {layer.displayName}
            </span>
          )}
        </div>

        <button
          type="button"
          onClick={(e) => toggleChl(e, layer)}
          disabled={!canModify}
          aria-pressed={layer.canHoldLuminaire}
          aria-label={`${t('workspace.chl')} ${layer.displayName}`}
          className={toggleClass}
        >
          <span
            className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full transition-transform ${
              layer.canHoldLuminaire ? 'bg-sky-500 translate-x-4' : 'bg-gray-400'
            }`}
          />
        </button>
        <button
          type="button"
          onClick={(e) => toggleLmn(e, layer)}
          disabled={!canModify}
          aria-pressed={layer.isIlluminated}
          aria-label={`${t('workspace.lmn')} ${layer.displayName}`}
          className={toggleClass}
        >
          <span
            className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full transition-transform ${
              layer.isIlluminated ? 'bg-lime-400 translate-x-4' : 'bg-gray-400'
            }`}
          />
        </button>
        <button
          type="button"
          onClick={(e) => openNorms(e, layer)}
          disabled={!layer.isIlluminated || !canModify}
          title={layer.customTargets?.length ? describeTargets(layer.customTargets) : undefined}
          className={`min-w-14 px-1 h-6 shrink-0 rounded text-[11px] transition-colors ${
            layer.isIlluminated && canModify ? 'hover:bg-white/10' : 'cursor-not-allowed opacity-50'
          } ${
            hasNorm
              ? 'text-lime-400 font-medium'
              : layer.isIlluminated
                ? 'text-amber-300 ring-1 ring-inset ring-amber-400/40'
                : 'text-gray-400'
          }`}
        >
          {hasNorm
            ? layer.selectedNorm === LAYER_NORM_CUSTOM
              ? t('workspace.customTargets.chip')
              : layer.selectedNorm
            : t('workspace.panel.layers.noNorm')}
        </button>
      </div>
    );
  };

  const toolbar = (
    <>
      <PanelSearch value={search} onChange={setSearch} placeholder={t('workspace.searchLayers')} />
      {modelLayers.length > 0 && (
        <div className="flex items-center gap-2.5 h-7 px-3 text-[11px] text-gray-400 border-t border-white/5">
          <button type="button" onClick={() => sortBy('type')} className="w-6 shrink-0 text-left hover:text-white">
            {t('workspace.type')}
            {sortArrow('type')}
          </button>
          <button type="button" onClick={() => sortBy('name')} className="flex-1 text-left hover:text-white">
            {t('workspace.layerName')}
            {sortArrow('name')}
          </button>
          <Tooltip title={t('workspace.chlTooltip')} arrow placement="top">
            <button type="button" onClick={() => sortBy('chl')} className="w-9 shrink-0 hover:text-white">
              {t('workspace.chl')}
              {sortArrow('chl')}
            </button>
          </Tooltip>
          <Tooltip title={t('workspace.lmnTooltip')} arrow placement="top">
            <button type="button" onClick={() => sortBy('lmn')} className="w-9 shrink-0 hover:text-white">
              {t('workspace.lmn')}
              {sortArrow('lmn')}
            </button>
          </Tooltip>
          <Tooltip
            title={t('workspace.normTooltip', 'Lighting standard applied to illuminated layer')}
            arrow
            placement="top"
          >
            <span className="w-14 shrink-0 text-center cursor-help">{t('workspace.norm')}</span>
          </Tooltip>
        </div>
      )}
    </>
  );

  return (
    <PanelSection
      title={t('workspace.layers')}
      count={search ? `${filteredLayers.length}/${modelLayers.length}` : modelLayers.length}
      toolbar={toolbar}
    >
      {modelLayers.length === 0 ? (
        <p className="px-4 py-6 text-[13px] text-gray-400">{t('workspace.panel.layers.empty')}</p>
      ) : (
        <div className="pb-3">
          <MeshGroupTree
            meshGroups={meshGroups}
            disabled={!canModify}
            onAddGroup={(parentGroupId) => {
              const group: MeshGroup = { uuid: crypto.randomUUID(), name: t('meshGroups.newGroup'), subgroups: [] };
              dispatch(addMeshGroup({ group, parentGroupId }));
            }}
            onRenameGroup={(uuid, name) => dispatch(renameMeshGroup({ uuid, name }))}
            onDeleteGroup={(uuid) => dispatch(deleteMeshGroup(uuid))}
            onDropLayerToGroup={(uuid, groupId) => dispatch(updateLayerGroupId({ uuid, groupId }))}
            getLayersForGroup={(groupId) =>
              filteredLayers.filter((l) => (groupId === null ? !l.groupId : l.groupId === groupId)).map((l) => l.uuid)
            }
            renderLayerItem={(layerUuid) => {
              const layer = filteredLayers.find((l) => l.uuid === layerUuid);
              if (!layer) return null;
              return (
                <React.Fragment>
                  {renderRow(layer)}
                  {normLayer === layer.threeJsName && layer.isIlluminated && (
                    <div className="px-3 py-3 border-b border-white/5 bg-black/30">
                      {layer.customTargets?.length ? (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            dispatch(updateLayerCustomTargets({ name: layer.threeJsName, targets: [] }));
                          }}
                          className="mb-2 text-[12px] text-gray-400 hover:text-white"
                        >
                          {t('workspace.customTargets.useClass')}
                        </button>
                      ) : (
                        <>
                          <p className="text-[12px] text-gray-300 mb-2">{t('workspace.chooseNormForLayer')}</p>
                          <div className="grid grid-cols-6 gap-1.5 mb-3">
                            {NORMS.map((norm) => (
                              <button
                                key={norm}
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  dispatch(updateLayerNorm({ name: layer.threeJsName, norm }));
                                  setNormLayer(null);
                                }}
                                style={{ backgroundColor: layer.selectedNorm === norm ? undefined : greyP5 }}
                                className={`h-7 px-1 rounded text-xs font-medium transition ${
                                  norm === LAYER_NORM_NONE ? 'col-span-2' : ''
                                } ${
                                  layer.selectedNorm === norm
                                    ? 'bg-lime-500 text-black'
                                    : 'text-white hover:!bg-lime-500 hover:text-black'
                                }`}
                              >
                                {norm}
                              </button>
                            ))}
                          </div>
                        </>
                      )}
                      <div className="mb-3">
                        <CustomTargetsForm
                          value={layer.customTargets ?? []}
                          onApply={(targets) =>
                            dispatch(updateLayerCustomTargets({ name: layer.threeJsName, targets }))
                          }
                        />
                      </div>
                      <LightingStandardsInfo />
                    </div>
                  )}
                </React.Fragment>
              );
            }}
          />
        </div>
      )}

      {typeMenu && (
        <div className="fixed inset-0 z-50" onClick={() => setTypeMenu(null)}>
          <div
            className="fixed min-w-[180px] rounded-md overflow-hidden border border-gray-600 shadow-[4px_6px_16px_rgba(0,0,0,0.6)]"
            style={{ top: typeMenu.top, left: typeMenu.left, backgroundColor: greyP5 }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-3 py-2 bg-black/30 text-xs font-medium text-gray-300">
              {t('workspace.panel.layers.setType')}
            </div>
            <div className="py-1">
              {(Object.keys(LAYER_TYPES) as (keyof typeof LAYER_TYPES)[]).map((type) => {
                const current = modelLayers.find((l) => l.threeJsName === typeMenu.layer)?.layerType === type;
                return (
                  <button
                    key={type}
                    type="button"
                    onClick={() => {
                      dispatch(updateLayerType({ name: typeMenu.layer, layerType: type }));
                      setTypeMenu(null);
                    }}
                    className={`w-full h-9 flex items-center gap-2 px-3 text-left text-sm transition ${
                      current ? 'bg-green02 text-black' : 'text-white hover:bg-green01 hover:text-black'
                    }`}
                  >
                    <LayerTypeIcon type={type} style={{ width: 14, height: 14, fill: 'currentColor' }} />
                    {type}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </PanelSection>
  );
}
